// Saved views in the sidebar (OEM-268): a "Views" heading under the Projects list (in the drawer on a
// phone) with the board's shared views first, then the person's own, each with a live count of the open
// items it matches across every project; "+ Save current view"; a "…" (or a right-click) per view for
// Rename, Edit filters, Make shared / personal, Move up / down and Delete; then the built-in Quick views.
// The view-mode bar on the board (Save changes, Save as new view, Reset to the view) is here too.

import { can, type BoardItem, type Viewer } from "@boem/core/shared";
import { quickItems, quickViewsFor, QUICK_PREFIX, viewCount, viewNameProblem, type SavedViewView, type ViewSpec } from "@boem/core/views";
import { useMemo, useRef, useState, type MouseEvent } from "react";
import { ApiError } from "../api.ts";
import { Icon } from "./Icons.tsx";
import { Menu, type MenuGroup } from "./Menu.tsx";
import { ErrorLine, Modal } from "./Modal.tsx";

/** What a view's row can do, from its "…" or a right-click. */
export interface ViewActions {
  onRename: (v: SavedViewView) => void;
  onEditFilters: (v: SavedViewView) => void;
  onShare: (v: SavedViewView, shared: boolean) => void;
  onMove: (v: SavedViewView, by: -1 | 1) => void;
  onDelete: (v: SavedViewView) => void;
}

/** The entries of a view's menu, for whoever looks. Pure (the tests read it). */
export function viewMenu(v: SavedViewView, list: SavedViewView[], viewer: Viewer, a: ViewActions): MenuGroup[] {
  const same = list.filter((x) => x.shared === v.shared);
  const i = same.findIndex((x) => x.id === v.id);
  const share = can(viewer, "view_shared");
  const groups: MenuGroup[] = [];
  if (v.canEdit) {
    groups.push({
      title: "View",
      entries: [
        { key: "rename", label: "Rename…", onSelect: () => a.onRename(v) },
        { key: "edit", label: "Edit filters", hint: "Open it, change the filters, then Save changes", onSelect: () => a.onEditFilters(v) },
        ...(share ? [{ key: "share", label: v.shared ? "Make personal" : "Make shared", hint: v.shared ? "Only you will see it" : "Everyone on the board sees it", onSelect: () => a.onShare(v, !v.shared) }] : []),
      ],
    });
    groups.push({
      title: "Order",
      entries: [
        { key: "up", label: "Move up", off: i <= 0 ? "It's first" : undefined, onSelect: () => a.onMove(v, -1) },
        { key: "down", label: "Move down", off: i >= same.length - 1 ? "It's last" : undefined, onSelect: () => a.onMove(v, 1) },
      ],
    });
    groups.push({ title: "Delete", entries: [{ key: "delete", label: "Delete view…", hint: v.shared ? "From everyone's sidebar" : undefined, onSelect: () => a.onDelete(v) }] });
  } else {
    groups.push({ title: "View", entries: [{ key: "edit", label: "Open it", hint: "Shared by a lead: Save as new view keeps your own copy", onSelect: () => a.onEditFilters(v) }] });
  }
  return groups;
}

function ViewRow({
  v,
  count,
  current,
  groups,
  onOpen,
}: {
  v: SavedViewView;
  count: number;
  current: boolean;
  groups: MenuGroup[];
  onOpen: () => void;
}) {
  const [open, setOpen] = useState(false);
  const more = useRef<HTMLButtonElement>(null);
  const onContext = (e: MouseEvent) => {
    e.preventDefault();
    setOpen(true);
  };
  return (
    <div className="sb-proj sb-vrow" data-current={current ? "1" : undefined} onContextMenu={onContext}>
      <button type="button" className="sb-row" aria-current={current ? "page" : undefined} title={`${v.name}${v.shared ? " (shared with the board)" : ""}: open it across every project`} onClick={onOpen}>
        <span className="sb-title">{v.name}</span>
        <span className="sb-meta">
          {v.shared && <span className="sb-own" title="Shared with the board">shared</span>}
          <span className="sb-n" title={`${count} open item${count === 1 ? "" : "s"} match it`}>
            {count}
          </span>
        </span>
      </button>
      <button ref={more} type="button" className="sb-panel" aria-label={`More for the view ${v.name}`} aria-haspopup="menu" aria-expanded={open} title="Rename, edit, share, reorder, delete" onClick={() => setOpen(!open)}>
        <Icon name="more" />
      </button>
      {open && <Menu anchor={more} groups={groups} label={`The view ${v.name}`} sheetHead={<b>{v.name}</b>} onClose={() => setOpen(false)} />}
    </div>
  );
}

/** The sidebar's Views: shared, then mine, then the quick views, each with its count of open items. */
export function SidebarViews({
  views,
  items,
  viewer,
  company,
  today,
  active,
  onOpen,
  onSaveCurrent,
  actions,
}: {
  views: SavedViewView[] | null;
  /** the board's items (every project, those with a board of their own too): the counts are worked out from them */
  items: BoardItem[];
  viewer: Viewer;
  /** OEMUAP: "PRs for my review" is offered too */
  company: boolean;
  today: string;
  /** the view open on the board ("quick:verify" for a quick one) */
  active: string | null;
  onOpen: (id: string) => void;
  onSaveCurrent?: () => void;
  actions: ViewActions;
}) {
  const list = views ?? [];
  const counts = useMemo(() => new Map(list.map((v) => [v.id, viewCount(items, v.spec, { me: viewer.name, meId: viewer.id, today })])), [list, items, viewer.name, viewer.id, today]);
  const quick = useMemo(
    () => quickViewsFor(company).map((q) => ({ ...q, count: quickItems(q.id, items, viewer, today).filter((i) => i.status === "open").length })),
    [company, items, viewer, today],
  );
  return (
    <div className="sb-views" role="group" aria-label="Views">
      <div className="sb-head">Views &middot; {list.length}</div>
      {list.map((v) => (
        <ViewRow key={v.id} v={v} count={counts.get(v.id) ?? 0} current={active === v.id} groups={viewMenu(v, list, viewer, actions)} onOpen={() => onOpen(v.id)} />
      ))}
      {views && !list.length && <p className="sb-empty">No saved views yet. Filter the board, then save it here.</p>}
      {onSaveCurrent && (
        <button type="button" className="sb-new" title="Save the board's filters, Group by and sort as a view across every project" onClick={onSaveCurrent}>
          + Save current view
        </button>
      )}
      <div className="sb-head sb-qhead">Quick views</div>
      {quick.map((q) => (
        <div key={q.id} className="sb-proj sb-vrow sb-quick" data-current={active === `${QUICK_PREFIX}${q.id}` ? "1" : undefined}>
          <button type="button" className="sb-row" aria-current={active === `${QUICK_PREFIX}${q.id}` ? "page" : undefined} title={q.hint} onClick={() => onOpen(`${QUICK_PREFIX}${q.id}`)}>
            <span className="sb-title">{q.name}</span>
            <span className="sb-meta">
              <span className="sb-n" data-hot={q.count && (q.id === "overdue" || q.id === "verify" || q.id === "review") ? "1" : undefined} title={`${q.count} open`}>
                {q.count}
              </span>
            </span>
          </button>
        </div>
      ))}
    </div>
  );
}

/** Save a view (new, or as a new one from a view), or rename one. */
export function SaveViewModal({
  title,
  initialName = "",
  spec,
  canShare,
  initialShared = false,
  rename = false,
  onSave,
  onClose,
}: {
  title: string;
  initialName?: string;
  /** what it saves (shown as a summary) */
  spec?: ViewSpec;
  canShare: boolean;
  initialShared?: boolean;
  /** only the name changes */
  rename?: boolean;
  onSave: (name: string, shared: boolean) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [shared, setShared] = useState(initialShared);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const why = name.trim() ? viewNameProblem(name) : null;
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave(name.trim(), shared);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal onClose={onClose}>
      <h3>{title}</h3>
      {!rename && (
        <p>
          A view runs over the whole board: every project, those with a board of their own too. It keeps {spec ? <b>{specSummary(spec)}</b> : "the filters"}; opening it from the sidebar puts the board back to exactly this.
        </p>
      )}
      <label htmlFor="view-name">Name</label>
      <input
        id="view-name"
        type="text"
        value={name}
        maxLength={60}
        placeholder="To verify"
        autoFocus
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && name.trim() && !why && !busy && void save()}
      />
      {why && <div className="hint">{why}</div>}
      {!rename && canShare && (
        <label className="fy-pause">
          <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
          Share it with the board (everyone sees it under Views)
        </label>
      )}
      <ErrorLine error={error} />
      <div className="row">
        <button className="btn" disabled={busy} onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={busy || !name.trim() || !!why} onClick={() => void save()}>
          {rename ? "Rename" : "Save view"}
        </button>
      </div>
    </Modal>
  );
}

const LANE_WORD: Record<string, string> = { todo: "To do", review: "PR review", deploy: "Deploy", verify: "Verify", closed: "Closed" };

/** "Verify · Kind: Bug · Due: Overdue · grouped by project": what a spec keeps, in a line. Pure. */
export function specSummary(s: ViewSpec): string {
  const bits = [
    s.lanes.length ? s.lanes.map((l) => LANE_WORD[l] ?? l).join(", ") : "every queue",
    s.asg.length ? `assigned: ${s.asg.map((a) => (a === "@me" ? "me" : a === "__none" ? "nobody" : a)).join(", ")}` : null,
    s.kind ? `kind: ${s.kind === "none" ? "not set" : s.kind}` : null,
    s.due ? `due: ${s.due === "week" ? "this week" : s.due === "none" ? "no date" : s.due}` : null,
    s.high ? "high" : null,
    s.tag ? `where: ${s.tag}` : null,
    s.role ? `who hits it: ${s.role}` : null,
    s.teams ? `Teams: ${s.teams}` : null,
    s.notReviewed ? "not yet reviewed" : null,
    s.nondev ? "non-dev" : null,
    s.drafts === "show" ? "drafts shown" : s.drafts === "only" ? "only drafts" : null,
    s.q ? `"${s.q}"` : null,
    // OEM-260: no project, or every project but the hidden ones
    s.project != null ? `project #${s.project}` : s.noProject ? "no project" : s.hideProjects?.length ? `every project but ${s.hideProjects.map((n) => `#${n}`).join(", ")}` : "every project",
    s.group !== "none" ? `grouped by ${s.group}` : null,
  ].filter(Boolean);
  return bits.join(" · ");
}

/** The board's line in view mode: what view this is, and (once a filter changed) Save changes / Save as new / Reset. */
export function ViewModeBar({
  name,
  quick,
  dirty,
  canSave,
  onSaveChanges,
  onSaveNew,
  onReset,
}: {
  name: string;
  quick: boolean;
  dirty: boolean;
  /** Save changes is offered: a saved view the viewer may change */
  canSave: boolean;
  onSaveChanges: () => void;
  onSaveNew: () => void;
  onReset: () => void;
}) {
  return (
    <div className="view-bar" role="group" aria-label={`The view ${name}`}>
      <span className="vb-lbl">
        <Icon name="search" /> {quick ? "Quick view" : "View"} &middot; <b>{name}</b> <span className="vb-all">across every project</span>
      </span>
      {dirty && (
        <span className="vb-acts">
          {canSave && (
            <button type="button" className="chip sm primary-chip" onClick={onSaveChanges}>
              Save changes
            </button>
          )}
          <button type="button" className="chip sm" onClick={onSaveNew}>
            Save as new view
          </button>
          <button type="button" className="chip sm" title="Back to the filters the view was saved with" onClick={onReset}>
            Reset to the view
          </button>
        </span>
      )}
    </div>
  );
}
